<template>
  <FormPage
    title="Компании"
    description="Список всех компаний в системе"
  >
    <template #controls>
      <button
        class="btn btn-primary"
        type="button"
        @click="router.push({ name: 'CompanyCreate' })"
      >+ Создать компанию</button>
    </template>

    <FormCard>
      <FormGrid :cols="3">
        <FormField label="Общий поиск">
          <input
            v-model.trim="filters.query"
            type="text"
            placeholder="Название, Директор, ОГРН, ИНН"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по названию">
          <input
            v-model.trim="filters.queryCompayName"
            type="text"
            placeholder="Название"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по Директору">
          <input
            v-model.trim="filters.queryDirector"
            type="text"
            placeholder="Директор"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по ОГРН">
          <input
            v-model.trim="filters.queryBSRN"
            type="text"
            placeholder="ОГРН"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по ИНН">
          <input
            v-model.trim="filters.queryINN"
            type="text"
            placeholder="ИНН"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Статус проверки ИНН">
          <select v-model="filters.INNStatus" @change="debouncedFetchFirstPage">
            <option v-for="opt in statusINNOptions" :key="opt.value" :value="opt.value">
              {{ opt.text }}
            </option>
          </select>
        </FormField>

        <FormField label="Статус проверки ATI">
          <select v-model="filters.ATIStatus" @change="debouncedFetchFirstPage">
            <option v-for="opt in statusATIOptions" :key="opt.value" :value="opt.value">
              {{ opt.text }}
            </option>
          </select>
        </FormField>

        <div class="filters-actions">
          <button class="btn" type="button" @click="resetFilters">Сбросить</button>
        </div>
      </FormGrid>
    </FormCard>

    <FormCard>
      <my-pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @pageChanged="pageChanged"
      />

      <my-loader v-if="isLoading" />

      <div v-else class="content">
        <table v-if="companies.length" class="company-table">
          <colgroup>
            <col class="col-name" />
            <col class="col-director" />
            <col class="col-BSRN" />
            <col class="col-INN" />
            <col class="col-INN-check-status" />
            <col class="col-ATI-check-status" />
            <col class="col-black-list" />
            <col class="col-clien-black-list" />
          </colgroup>
          <thead>
            <tr>
              <th>Название</th>
              <th>Директор</th>
              <th>ОГРН</th>
              <th>ИНН</th>
              <th>Статус проверки ИНН</th>
              <th>Статус проверки ATI</th>
              <th>Черный список</th>
              <th>Клиентский ЧС</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="comp in companies"
              :key="comp.id"
              class="row-card"
              @click="$router.push(`/companies/${comp.id}`)"
            >
              <td :title="comp.name">{{ comp.name || '—' }}</td>
              <td :title="comp.director">{{ comp.director || '—' }}</td>
              <td :title="comp.ogrn">{{ comp.ogrn || '—' }}</td>
              <td :title="comp.inn">{{ comp.inn || '—' }}</td>
              <td>
                <span :class="['status-badge', comp.security_info?.dadata_is_end ? 'st-verified' : 'st-nverified']">
                  {{ comp.security_info?.dadata_is_end ? 'Проверено' : 'Не проверено' }}
                </span>
              </td>
              <td>
                <span :class="['status-badge', comp.security_info?.ati_is_end ? 'st-verified' : 'st-nverified']">
                  {{ comp.security_info?.ati_is_end ? 'Проверено' : 'Не проверено' }}
                </span>
              </td>
              <td>{{ comp.security_info?.black_list ? '📛' : '—' }}</td>
              <td>{{ comp.security_info?.black_list_client ? '📛' : '—' }}</td>
            </tr>
          </tbody>
        </table>

        <div v-else class="state-block">
          Компаний по выбранным фильтрам нет.
        </div>
      </div>
    </FormCard>
  </FormPage>
</template>

<script>
import { useStore } from 'vuex'
import { ref, computed, onMounted, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import FormPage from '@/components/formKit/FormPage.vue'
import FormCard from '@/components/formKit/FormCard.vue'
import FormGrid from '@/components/formKit/FormGrid.vue'
import FormField from '@/components/formKit/FormField.vue'

export default {
  name: 'CompanyList',
  components: {
    FormPage,
    FormCard,
    FormGrid,
    FormField,
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()

    const companies = computed(() => store.state.apiStorage.companies.items || [])
    const isLoading = computed(() => store.state.apiStorage.companies.statusLoading)
    const currentPage = computed(() => store.state.apiStorage.companies.currentPage)
    const totalPages = computed(() => store.state.apiStorage.companies.totalPages)

    const loadData = (page) => {
      store.dispatch('apiStorage/getFilteredList', { model: 'companies', params: { page, ...filterParams.value } })
    }

    const filters = reactive({
      query: route.query.query || '',
      queryDirector: route.query.queryDirector || '',
      queryCompayName: route.query.queryCompayName || '',
      queryBSRN: route.query.queryBSRN || '',
      queryINN: route.query.queryINN || '',
      ATIStatus: ['0', '-1'].includes(route.query.ATIStatus) ? Number(route.query.ATIStatus) : 1,
      INNStatus: ['0', '-1'].includes(route.query.INNStatus) ? Number(route.query.INNStatus) : 1,
    })

    const statusATIOptions = ref([
      { text: 'Все записи', value: 1 },
      { text: 'Проверено', value: 0 },
      { text: 'Не проверено', value: -1 },
    ])

    const statusINNOptions = ref([
      { text: 'Все записи', value: 1 },
      { text: 'Проверено', value: 0 },
      { text: 'Не проверено', value: -1 },
    ])

    const resetFilters = () => {
      filters.query = ''
      filters.queryDirector = ''
      filters.queryCompayName = ''
      filters.queryBSRN = ''
      filters.queryINN = ''
      filters.ATIStatus = 1
      filters.INNStatus = 1
      fetchFirstPage()
    }

    const filterParams = computed(() => {
      const params = {}

      if (filters.query) {
        params.search = filters.query.toLowerCase()
      }
      if (filters.queryDirector) {
        params.director = filters.queryDirector.toLowerCase()
      }
      if (filters.queryCompayName) {
        params.name_contains = filters.queryCompayName.toLowerCase()
      }
      if (filters.queryBSRN) {
        params.ogrn = filters.queryBSRN.toLowerCase()
      }
      if (filters.queryINN) {
        params.inn_contains = filters.queryINN.toLowerCase()
      }

      if (filters.ATIStatus === 0) {
        params.ati_is_end = true
      } else if (filters.ATIStatus === -1) {
        params.ati_is_end = false
      }

      if (filters.INNStatus === 0) {
        params.dadata_is_end = true
      } else if (filters.INNStatus === -1) {
        params.dadata_is_end = false
      }

      return params
    })

    onMounted(() => {
      const pageFromURL = Number(route.query.page) || 1
      loadData(pageFromURL)
    })

    const pageChanged = (page) => {
      loadData(page)
    }

    const updateURL = () => {
      router.replace({ query: { ...filters } }).catch(() => {})
    }

    const fetchFirstPage = () => {
      loadData(1)
      updateURL()
    }

    const debouncedFetchFirstPage = useDebounceFn(fetchFirstPage, 300)

    return {
      router,
      companies,
      filters,
      filterParams,
      isLoading,
      statusATIOptions,
      statusINNOptions,
      totalPages,
      currentPage,
      debouncedFetchFirstPage,
      updateURL,
      loadData,
      pageChanged,
      resetFilters,
    }
  },
}
</script>

<style scoped>
.btn {
  border: 1px solid #d7dce2;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  height: 32px;
  border-radius: 4px;
  padding: 0 12px;
}

.btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.btn-primary {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.btn-primary:hover {
  background: #0056b3;
  border-color: #0056b3;
  color: #fff;
}

.filters-actions {
  display: flex;
  align-items: flex-end;
}

.content {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  width: 100%;
}

.state-block {
  padding: 32px;
  color: #667085;
  font-size: 14px;
}

.company-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
}

.company-table th,
.company-table td {
  height: 32px;
  padding: 6px 8px;
  border-bottom: 1px solid #edf0f3;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.company-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f7f8fa;
  color: #444;
  font-weight: bold;
}

.company-table td {
  font-weight: bold;
}

.row-card {
  cursor: pointer;
}

.row-card:hover {
  background-color: rgba(224, 187, 72, 0.4);
}

.status-badge {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  border-radius: 10px;
  padding: 2px 7px;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
}

.st-verified {
  background: #e6f4ea;
  color: #1e7e34;
}

.st-nverified {
  background: #fdeaea;
  color: #c0392b;
}
</style>
